<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import BigNumber from '@exchange/helpers/bignumber';
import { OrderSide, OrderType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';

interface AmountToReceive {
  value: BigNumber;
  code: string;
  precision: number;
}

interface SideFee {
  value: BigNumber;
  code: string;
  precision: number;
}

const props = defineProps({
  feeValue: { type: Number, required: false },
  side: { type: String as PropType<OrderSide>, required: true },
  sideAmount: { type: Object as PropType<AmountToReceive>, required: false },
  sideFee: { type: Object as PropType<SideFee>, required: false },
  type: { type: String as PropType<OrderType>, required: true },
});

const { t } = useI18n({ useScope: 'global' });

const currentSideFeeDefined = computed(() => Boolean(props.sideFee));

const feeLabel = computed(() => {
  let feeValueString = t('fundamentals.emptyValues.default');

  if (props.feeValue !== undefined) {
    feeValueString = props.feeValue.toFixed(CONSTANTS.PRECISION.DEFAULT_FEE);
  }

  return t('modules.orderForm.fee', {
    feeValue: feeValueString,
  });
});

const feeAmount = computed(() => ({
  number: props.sideFee?.value,
  suffix: props.sideFee?.code,
  precision: props.sideFee?.precision,
}));

const estimationPrefix = computed(() => (props.type.toUpperCase() === OrderType.MARKET && props.side === OrderSide.SELL ? '~ ' : ''));
</script>

<template>
  <div class="order-form-fees">
    <div class="order-form-fees__label">
      <span testid="order-fee-label">{{ feeLabel }}</span>
    </div>
    <ot-number-display
      v-if="currentSideFeeDefined"
      class="order-form-fees__number user-select"
      :grouping="true"
      :number="feeAmount.number"
      :precision="feeAmount.precision"
      :prefix="estimationPrefix"
      :suffix="feeAmount.suffix"
      testid="order-fee-value"
    />
    <div
      v-else
      class="order-form-fees__number"
    >
      {{ $t('fundamentals.emptyValues.default') }}
    </div>
    <div class="order-form-fees__label">{{ $t('modules.orderForm.amountToReceive') }}</div>
    <ot-number-display
      v-if="currentSideFeeDefined"
      class="order-form-fees__number user-select"
      :grouping="true"
      :number="sideAmount && sideAmount.value"
      :precision="sideAmount && sideAmount.precision"
      :prefix="estimationPrefix"
      :suffix="sideAmount && sideAmount.code"
      testid="order-total-value"
    />
    <div
      v-else
      class="order-form-fees__number"
    >
      {{ $t('fundamentals.emptyValues.default') }}
    </div>
  </div>
</template>

<style lang="scss">
.order-form-fees {
  display: grid;
  font-size: var(--font-size-sm);
  grid-gap: 11px;
  grid-template-columns: [field] min-content [amount] auto;
  grid-template-rows: 1fr 1fr;
  place-items: center start;

  &__number {
    justify-self: end;
    white-space: nowrap;
  }

  &__label {
    display: grid;
    color: rgb(var(--v-theme-text-secondary));
    grid-column-gap: 5px;
    grid-template-columns: [fee] min-content;
    white-space: nowrap;
  }

  .xs & {
    display: block;

    .order-form-fees__label {
      margin-bottom: 2px;
    }

    .order-form-fees__number {
      display: block;
      margin-bottom: 4px;
      text-align: left;
    }
  }
}
</style>
